import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Row, Col,
  Alert, message,
  SuccessNotification,
  Input, Radio, Select,
  Popconfirm, Button,
  DatePicker,
} from "components/common";
import { callPutApiWithAuth } from "utils/api";
import { countryStates, statesByCountry } from "common/country";

export default function MassPayCard({
  isActive,
  provider,
  loadListData,
}) {
  const sudoName = useSelector((state) => state.auth.sudo_name);
  const myUser = useSelector((state) => state.auth.user);
  const [formData, setFormData] = useState({
    country: "",
    state: "",
    city: "",
    zipcode: "",
    address: "",
    address_line2: "",
    birthday: ""
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const onFormInput = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const onSaveDone = () => {
    setIsUpdating(false);
    SuccessNotification("Mass Payout has been set as your payout provider.");
    loadListData();
  };

  const handleSave = () => {
    // check validation
    if (
      !formData.country ||
      !formData.state ||
      !formData.city ||
      !formData.address ||
      !formData.zipcode ||
      !formData.birthday
    ) {
      message.error("Input all required fields");
      return;
    }

    setIsUpdating(true);
    const body = sudoName
      ? {
          form_info: {
            ...formData,
            sudo_name: sudoName,
          },
        }
      : {
          form_info: formData,
        };
    callPutApiWithAuth(
      `my_account/payment_option/${provider.id}/activate`,
      body,
      onSaveDone,
      () => setIsUpdating(false)
    );
  };

  useEffect(() => {
    if (provider && myUser) {
      if (provider.form_info) {
        setFormData({
          ...formData,
          country: provider.form_info.country || "",
          state: provider.form_info.state || "",
          city: provider.form_info.city || "",
          address: provider.form_info.address || "",
          address_line2: provider.form_info.address_line2 || "",
          zipcode: provider.form_info.zipcode || "",
          birthday: provider.form_info.birthday || ""
        });
      } else {
        if (myUser.shipping_details.length > 0) {
          const shippingDetail = myUser.shipping_details[0];
          setFormData({
            ...formData,
            country: shippingDetail.shipping_country || "",
            state: shippingDetail.shipping_state || "",
            city: shippingDetail.shipping_city || "",
            address: shippingDetail.shipping_address || "",
            address_line2: shippingDetail.shipping_address_line2 || "",
            zipcode: shippingDetail.shipping_zipcode || "",
          });
        } else if (myUser.billing_details.length > 0) {
          const billingDetail = myUser.billing_details[0];
          setFormData({
            ...formData,
            country: billingDetail.billing_country || "",
            state: billingDetail.billing_state || "",
            city: billingDetail.billing_city || "",
            address: billingDetail.billing_address || "",
            address_line2: billingDetail.billing_address_line2 || "",
            zipcode: billingDetail.billing_zipcode || "",
          });
        }
      }
    }
  }, [provider, myUser]);

  return (
    <div className="payment-card-section">
      <div className="payment-title">
        <span>Mass Payment</span>
        {isActive && <span className="active-badge">ACTIVE</span>}
      </div>
      <div className="payment-form">
        <Row gutter={[12, 8]}>
          <Col xs={24}>
            <div className="payment-form-text" style={{ marginBottom: 10 }}>
              Your Address
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="form-group">
              <Select
                label="Country*"
                value={formData.country}
                onChange={(v) => onFormInput("country", v)}
                options={countryStates.map((el) => ({
                  label: el.name,
                  value: el.code2,
                }))}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="form-group">
              <Select
                label="State*"
                value={formData.state}
                onChange={(v) => onFormInput("state", v)}
                options={statesByCountry(formData.country).map((el) => ({
                  label: el.name,
                  value: el.name,
                }))}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="form-group">
              <Input
                label="City*"
                value={formData.city}
                onChange={(e) => onFormInput("city", e.target.value)}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="form-group">
              <Input
                label="Address*"
                value={formData.address}
                onChange={(e) => onFormInput("address", e.target.value)}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="form-group">
              <Input
                label="Address 2"
                value={formData.address_line2}
                onChange={(e) => onFormInput("address_line2", e.target.value)}
              />
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div className="form-group">
              <Input
                label="Postal Code*"
                value={formData.zipcode}
                onChange={(e) => onFormInput("zipcode", e.target.value)}
              />
            </div>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <div className="form-group">
              <DatePicker
                label="Birthday*"
                value={formData.birthday}
                onChange={(value, val) => onFormInput("birthday", val)}
              />
            </div>
          </Col>
        </Row>
        {!isActive &&  (
          <Row gutter={[8, 8]}>
            <Col xs={24}>
              <div style={{ height: 24 }} />
              <div className="action-group">
                <Popconfirm onConfirm={handleSave}>
                  <button loading={isUpdating}>
                    {provider.account ? `Activate` : `Save & Activate`}
                  </button>
                </Popconfirm>
              </div>
            </Col>
          </Row>
        )}
      </div>
      <div className="alert-container-section">
        <div className="alert-section">
          <Alert
            style={{ margin: "5px 0px 5px", alignItems: "baseline" }}
            message={`Please note: Make sure all of your information is ACCURATE. If you put in the wrong checking account number or routing number, the payment could be sent to the wrong bank account and we will not be able to recover your funds.`}
            type={"info"}
            showIcon
          />{" "}
        </div>
        <div className="alert-section">
          <Alert
            style={{ margin: "5px 0px 5px", alignItems: "baseline" }}
            message={
              "If you have selected direct deposit, it could take 2-3 business days for you to receive your earnings depending on your bank."
            }
            type={"info"}
            showIcon
          />
        </div>
      </div>
    </div>
  );
}
